import { Box, Button, css, Stack, styled, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { RadioButtonsCommute } from '@/components/form-elements/RadioButtonsCommute';
import { SliderDistance } from '@/components/form-elements/SliderDistance';
import { useQueryMonitoringLocation } from '@/hooks/queries/useQueryMonitoringLocation';
import {
  ApiError,
  CommuteType,
  CreateMonitoringDto,
  Monitoring,
  MonitoringLinkType,
  MonitoringService,
} from '@/services/api';
import { useGlobalStore } from '@/stores/useGlobalStore';

const CardTitle = styled(Box)(
  ({ theme }) => css`
    background-color: ${theme.palette.primary.main};
    border-radius: ${theme.spacing(2)} ${theme.spacing(2)} 0 0;
    padding: ${theme.spacing(2)};
    color: ${theme.palette.primary.contrastText};
    justify-content: space-between;
    align-items: center;

    ${theme.breakpoints.up('md')} {
      display: flex;
      padding: ${theme.spacing(3)} ${theme.spacing(4)};
    }
  `,
) as typeof Box;

type FormData = Omit<CreateMonitoringDto, 'locationId'>;

export function MonitoringPage() {
  const [throttleError, setThrottleError] = useState(false);

  const monitoringThrottle = useGlobalStore((state) => state.monitoringThrottle);
  const { addMonitoringThrottle } = useGlobalStore((state) => state.actions);

  const navigate = useNavigate();
  const { monitoringId } = useParams();
  const { control, watch, handleSubmit, reset } = useForm<FormData>({
    defaultValues: { distance: 0 },
  });

  const [searchParams] = useSearchParams();
  const language = searchParams.get('language') || 'de';
  const { t, i18n } = useTranslation();

  if (i18n.language !== language) i18n.changeLanguage(language);

  const { data: location, isLoading } = useQueryMonitoringLocation();

  const { mutate } = useMutation<Monitoring, ApiError, FormData>({
    mutationFn: (formData: FormData) => {
      if (location?.linkType === MonitoringLinkType.SMARTPHONE) {
        return MonitoringService.createMobile({ requestBody: { ...formData, locationId: location.locationId } });
      }

      return MonitoringService.create({ requestBody: { ...formData, locationId: location!.locationId } });
    },
    onSuccess: () => {
      if (location?.linkType === MonitoringLinkType.SMARTPHONE) {
        addMonitoringThrottle(monitoringId!);
      }

      reset();
      navigate(`/monitoring/${monitoringId}/daily`);
    },
    onError: (error) => {
      if (error.status === 429) {
        setThrottleError(true);
      }
    },
  });

  return isLoading ? null : (
    <>
      {location?.monitoringActive && !throttleError ? (
        <form
          name="monitoring-form"
          onSubmit={handleSubmit((submitData) => {
            if (
              location.linkType !== MonitoringLinkType.SMARTPHONE ||
              !monitoringThrottle[monitoringId!] ||
              dayjs(monitoringThrottle[monitoringId!]).isBefore(dayjs().subtract(1, 'hour'))
            ) {
              mutate(submitData);
            } else {
              setThrottleError(true);
            }
          })}
        >
          <CardTitle>
            <Typography variant="h2" component="h1">
              {location.monitoringTitle}
            </Typography>
            <Typography>{location?.name}</Typography>
          </CardTitle>

          <Stack sx={{ padding: { xs: 2, md: 4 } }} gap={4}>
            <RadioButtonsCommute
              name="commuteType"
              control={control}
              label={location.monitoringQuestion ?? t('MonitoringCommuteQuestion')}
              commuteTypes={location.monitoringCommuteTypes as CommuteType[]}
            />

            <SliderDistance
              name="distance"
              control={control}
              label={t('MonitoringDistanceQuestion')}
              subLabel={t('MonitoringDistanceLabel')}
              maxValue={location.monitoringScaleMax}
            />

            <Stack alignItems="center">
              <Button
                variant="contained"
                size="large"
                sx={{
                  paddingX: 8,
                }}
                type="submit"
                disabled={!watch('commuteType')}
              >
                {t('MonitoringButtonConfirm')}
              </Button>
            </Stack>
          </Stack>
        </form>
      ) : (
        <>
          <CardTitle>
            <Typography variant="h2" component="h1">
              {t('MonitoringLimitReached')}
            </Typography>
          </CardTitle>
          <Box sx={{ padding: 3 }}>
            <Typography>{t('MonitoringLimitReachedDescription')}</Typography>

            <Stack direction="row" gap={4} marginTop={4} justifyContent="space-between">
              <Button
                size="large"
                variant="outlined"
                onClick={() => {
                  setThrottleError(false);
                  reset();
                }}
              >
                {t('MonitoringButtonBack')}
              </Button>
              <Button
                size="large"
                variant="contained"
                onClick={() => {
                  reset();
                  navigate(`/monitoring/${monitoringId}/daily`);
                }}
              >
                {t('MonitoringButtonOverview')}
              </Button>
            </Stack>
          </Box>
        </>
      )}

      {location && !location.monitoringActive && (
        <>
          <CardTitle sx={{ display: 'flex' }}>
            <Typography variant="h2" component="h1">
              {t('MonitoringNotActive')}
            </Typography>
          </CardTitle>
          <Box sx={{ padding: 3 }}>
            <Typography>{t('MonitoringNotActiveDescription')}</Typography>
          </Box>
        </>
      )}
    </>
  );
}
